<template>
  <div class="mall-cool-player" :class="`theme-${theme}`">
    <audio :src="source | replaceUrlProtocol" ref="player" v-if="isAudio"></audio>
    <template v-else-if="cover">
      <div class="cover" @click="playVideo">
        <div class="inner">
          <img :src="cover | replaceUrlProtocol">
        </div>
      </div>

      <mall-player-dialog v-model="showVideo" :src="source" theme="video" />
    </template>
  </div>
</template>

<script>
import Plyr from 'plyr/dist/plyr.mjs';
import 'plyr/src/sass/plyr.scss?no-px2rem';
import MallPlayerDialog from './PlayerDialog';
import { trackElClickH5 } from '@changba/funny';

export default {
  name: 'MallCoolPlayer',

  components: {
    MallPlayerDialog
  },

  props: {
    src: {
      type: [String, Array],
      default: ''
    },

    cover: {
      type: String,
      default: ''
    },

    theme: {
      type: String,
      default: '1'
    },

    stats: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      source: '',
      currentTime: 0,
      showVideo: false
    };
  },

  computed: {
    isAudio() {
      return /\.mp3(?:$|\?)/i.test(this.source);
    }
  },

  watch: {
    src(value) {
      this.stop();
      this.source = typeof value === 'string' ? value : value[0];
    }
  },

  created() {
    if (this.src) {
      this.source = typeof this.src === 'string' ? this.src : this.src[0];
    }
  },

  mounted() {
    if (this.isAudio) {
      this.player = new Plyr(this.$refs.player, {
        controls: ['play-large', 'play', 'progress', 'current-time', 'duration'],
        iconUrl: '//res.cdn.changbaimg.com/-/45328fbd01e8be57/plyr.svg'
      });

      this.player.on('play', () => {
        this.stopAll(this.player.media);
        trackElClickH5(`Cool播放器播放-${this.stats}`);
      });
      this.player.on('pause', () => {
        trackElClickH5(`Cool播放器暂停-${this.stats}`);
      });
    }
  },

  methods: {
    stopAll(audio) {
      const nodes = document.querySelectorAll('audio');

      nodes.forEach((item) => {
        if (item !== audio) {
          item?.pause();
        }
      });
    },

    stop() {
      this.player?.stop();
    },

    play() {
      //this.stopAll();
      this.$nextTick(() => {
        this.player?.play();
      });
    },

    playVideo() {
      this.stopAll();
      this.showVideo = true;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@cdn_css/helper.scss";

$play: "~@cdn_img/play-1.svg";
$stop: "~@cdn_img/stop-1.svg";

.mall-cool-player {
  position: relative;
  width: 100%;
  border-radius: 4px;

  &::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
    width: 18px;
    font-weight: bold;
    font-size: 10px;
    text-align: center;
    border-radius: 4px 0 0 4px;
    pointer-events: none;
  }

  .cover {
    padding-left: 18px;

    .inner {
      position: relative;
      width: 44px;
      height: 44px;

      &:before {
        position: absolute;
        width: 21px;
        height: 21px;
        content: "";
        @extend %center-xy;
        @include bg-img("~@cdn_img/play-blur.svg", contain);
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
      }
    }
  }

  --plyr-range-track-height: 2px;
  --plyr-range-thumb-height: 7px;
  --plyr-audio-range-track-background: #ddd;

  &::v-deep {
    .plyr--audio {
      background-color: #f5f5f5;

      .plyr__controls {
        padding-top: 10px;
        padding-right: 10px;
        padding-bottom: 10px;
        padding-left: 26px;
        background-color: transparent;

        .plyr__progress__container {
          padding-left: 8px;
        }

        .plyr__control {
          padding: 0;

          svg {
            use {
              display: none;
            }
          }

          &.plyr__tab-focus,
          &:hover {
            background: transparent;
            box-shadow: none;
          }

          .icon--not-pressed {
            width: 24px;
            height: 24px;
          }

          .icon--pressed {
            width: 24px;
            height: 24px;
          }
        }

        .plyr__time {
          padding-right: 0;
          padding-left: 6px;
          color: #aaa;
          font-size: 8px;
          font-family: DINAlternate-Bold;

          + .plyr__time {
            margin-left: 0;
            padding-left: 0;

            &:before {
              margin-right: 0;
              content: '/';
            }
          }

          @media (max-width: 767px) {
            + .plyr__time {
              display: block;
            }
          }
        }
      }
    }
  }

  &.theme-1 {
    --plyr-color-main: #ff9a43;
    --plyr-range-thumb-background: #ff9a43;
    --plyr-range-thumb-shadow: 0 1px 1px rgba(255, 154, 67, 0.2), 0 0 0 1px rgba(255, 154, 67, 0.2);

    &::before {
      padding-top: 5px;
      color: #fff;
      line-height: 1.1;
      background: #fd8f45;
      content: "原作品";
    }

    &::v-deep {
      .plyr--audio {
        .plyr__controls {
          .plyr__control {
            .icon--not-pressed {
              -svg-mixer-fill: #ff9a43 path;
              -svg-mixer-stroke: #ff9a43 circle;
              @include bg-img($play, 100%);
            }

            .icon--pressed {
              -svg-mixer-fill: #fd8f45 ".inner";
              -svg-mixer-stroke: #fd8f45 circle;
              @include bg-img($stop, 100%);
            }
          }
        }
      }
    }
  }

  &.theme-2 {
    --plyr-color-main: #fec65c;
    --plyr-range-thumb-background: #fec65c;
    --plyr-range-thumb-shadow: 0 1px 1px rgba(254, 198, 92, 0.2), 0 0 0 1px rgba(254, 198, 92, 0.2);

    &::before {
      padding-top: 7px;
      color: #fff;
      line-height: 1.5;
      background: #ffbd40;
      content: "干声";
    }

    &::v-deep {
      .plyr--audio {
        .plyr__controls {
          .plyr__control {
            .icon--not-pressed {
              -svg-mixer-fill: #ffbd40 path;
              -svg-mixer-stroke: #ffbd40 circle;
              @include bg-img($play, 100%);
            }

            .icon--pressed {
              -svg-mixer-fill: #ffbd40 ".inner";
              -svg-mixer-stroke: #ffbd40 circle;
              @include bg-img($stop, 100%);
            }
          }
        }
      }
    }
  }

  &.theme-3 {
    --plyr-color-main: #d1beb2;
    --plyr-range-thumb-background: #d1beb2;
    --plyr-range-thumb-shadow: 0 1px 1px rgba(254, 198, 92, 0.2), 0 0 0 1px rgba(254, 198, 92, 0.2);

    &::before {
      padding-top: 7px;
      color: #fff;
      line-height: 1.5;
      background: #b9a599;
      content: "备用";
    }

    &::v-deep {
      .plyr--audio {
        .plyr__controls {
          .plyr__control {
            .icon--not-pressed {
              -svg-mixer-fill: #b9a599 path;
              -svg-mixer-stroke: #b9a599 circle;
              @include bg-img($play, 100%);
            }

            .icon--pressed {
              -svg-mixer-fill: #b9a599 ".inner";
              -svg-mixer-stroke: #b9a599 circle;
              @include bg-img($stop, 100%);
            }
          }
        }
      }
    }
  }

  &.theme-4 {
    --plyr-color-main: #ff3348;
    --plyr-range-thumb-background: #ff3348;
    --plyr-range-thumb-shadow: 0 1px 1px rgba(255, 51, 72, 0.2), 0 0 0 1px rgba(255, 51, 72, 0.2);

    &::before {
      display: none;
    }

    .cover {
      padding-left: 0;

      img {
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      }
    }

    &::v-deep {
      .plyr--audio {
        .plyr__controls {
          padding-left: 12px;

          .plyr__control {
            .icon--not-pressed {
              -svg-mixer-fill: #fff path, #ff3348 circle;
              -svg-mixer-stroke: #ff3348 circle;
              @include bg-img($play, 100%);
            }

            .icon--pressed {
              -svg-mixer-fill: #fff ".inner", #ff3348 circle;
              -svg-mixer-stroke: #ff3348 circle;
              @include bg-img($stop, 100%);
            }
          }
        }
      }
    }
  }
}
</style>
